import RControlledDialog from "@/components/RComponents/RControlledDialog";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import RFlex from "@/components/RComponents/RFlex";
import { Eye, EyeClosed } from "lucide-react";
import RButton from "@/components/RComponents/RButton";
import { changeMyPasswordValidation } from "@/utils/validations";
import { useResetMyPassword } from "../hooks/authHooks";

interface ResetPaswordProps {
    isResetOpen: boolean;
    handleIsResetOpen: (isOpen: boolean) => void;
}

const ResetMyPassowrd: React.FC<ResetPaswordProps> = ({ isResetOpen, handleIsResetOpen }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showOldPassword, setShowOldPassword] = useState(false);

    const { t } = useTranslation();
    const form = useForm<any>({
        resolver: zodResolver(changeMyPasswordValidation),
        defaultValues: {
            oldPassword: "",
            password: "",
            confirmPassword: "",
        },
    });

    const { mutate: resetMyPassword, isPending: isResetPasswordPending } = useResetMyPassword({
        displaySuccess: true,
        successMessage: t("your_password_changed_successfully"),
        onSuccessFn: () => {
            handleIsResetOpen(false);
            form.reset();
        }
    });

    const onCancel = () => {
        form.reset();
        handleIsResetOpen(false);
    }

    const onSubmit = (data: any) => {
        const formData = new FormData();
        formData.append("oldPassword", data.oldPassword);
        formData.append("password", data.password);
        formData.append("confirmPassword", data.confirmPassword);
        resetMyPassword(formData);
    }

    return (
        <RControlledDialog
            isOpen={isResetOpen}
            closeDialog={() => handleIsResetOpen(false)}
            dialogHeader={{ title: t("change_password"), description: t("you_are_changing_your_password_please_confirm_this_action") }}
            dialogBody={
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="oldPassword"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <div className="relative">
                                            <Input
                                                className="shad-input"
                                                type={showOldPassword ? "text" : "password"}
                                                placeholder={t("old_password")}
                                                autoComplete="new-password"
                                                {...field}
                                            />
                                            <button
                                                type="button"
                                                className="absolute inset-y-0 right-2 flex items-center"
                                                onClick={() => setShowOldPassword(!showOldPassword)}
                                            >
                                                {showOldPassword ? <Eye className="h-5 w-5" /> : <EyeClosed className="h-5 w-5" />}
                                            </button>
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="password"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <div className="relative">
                                            <Input
                                                className="shad-input"
                                                type={showPassword ? "text" : "password"}
                                                placeholder={t("password")}
                                                autoComplete="new-password"
                                                {...field}
                                            />
                                            <button
                                                type="button"
                                                className="absolute inset-y-0 right-2 flex items-center"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? <Eye className="h-5 w-5" /> : <EyeClosed className="h-5 w-5" />}
                                            </button>
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="confirmPassword"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <div className="relative">
                                            <Input
                                                className="shad-input"
                                                type={showConfirmPassword ? "text" : "password"}
                                                placeholder={t("confirm_password")}
                                                autoComplete="new-password"
                                                {...field}
                                            />
                                            <button
                                                type="button"
                                                className="absolute inset-y-0 right-2 flex items-center"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            >
                                                {showConfirmPassword ? <Eye className="h-5 w-5" /> : <EyeClosed className="h-5 w-5" />}
                                            </button>
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <RFlex className="justify-end gap-2">
                            <RButton text={t("cancel")} type="button" variant="outline" onClick={onCancel} />

                            <RButton
                                text={t("change_password")}
                                type="submit"
                                className="shad-bg-[#71CEE2]"
                                loading={isResetPasswordPending}
                                disabled={isResetPasswordPending}
                            />
                        </RFlex>
                    </form>
                </Form>
            }
        />
    );
};

export default ResetMyPassowrd;
